import { Component, OnInit, ElementRef, AfterViewInit, Input, HostListener, AfterViewChecked, viewChild, output, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LabelElement, LabelLayoutConfig } from 'src/app/services/niimbot-printer/niimbot-printer.models';
import * as QRCode from 'qrcode';
import {FormsModule} from "@angular/forms";
import {DecimalPipe} from "@angular/common";
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideAlignCenter,
  lucideAlignHorizontalJustifyCenter,
  lucideAlignLeft,
  lucideAlignRight,
  lucideAlignVerticalJustifyCenter,
  lucideBold,
  lucideItalic,
  lucideMinus,
  lucideQrCode,
  lucideSettings2,
  lucideTrash2,
  lucideType,
} from '@ng-icons/lucide';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmInput } from '@spartan-ng/helm/input';

type DragMode = 'move' | 'resize-br' | 'resize-bl' | 'resize-tr' | 'resize-tl'
  | 'resize-r' | 'resize-l' | 'resize-t' | 'resize-b';

const HANDLE_SIZE = 8;

@Component({
  selector: 'app-label-editor',
  templateUrl: './label-editor.component.html',
  imports: [
    FormsModule,
    DecimalPipe,
    NgIcon,
    HlmButton,
    HlmInput
  ],
  providers: [
    provideIcons({
      lucideType,
      lucideMinus,
      lucideQrCode,
      lucideTrash2,
      lucideSettings2,
      lucideBold,
      lucideItalic,
      lucideAlignLeft,
      lucideAlignCenter,
      lucideAlignRight,
      lucideAlignHorizontalJustifyCenter,
      lucideAlignVerticalJustifyCenter,
    }),
  ]
})
export class LabelEditorComponent implements OnInit, AfterViewInit, AfterViewChecked {
  private http = inject(HttpClient);

  readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
  readonly inlineEditorInputRef = viewChild<ElementRef<HTMLInputElement>>('inlineEditorInput');

  @Input() qrText?: string;
  @Input() participantName?: string;
  @Input() loteName?: string;

  readonly layoutChange = output<LabelLayoutConfig>();
  readonly participantNameChange = output<string>();
  readonly loteNameChange = output<string>();

  // Local overrides set by double-click editing (not persisted to layout)
  overrideParticipantName: string | null = null;
  overrideLoteName: string | null = null;

  editingOverlay: {
    field: 'name' | 'lote';
    value: string;
    rect: { left: number; top: number; width: number; height: number };
    fontSize: number;
  } | null = null;

  private pendingFocusOverlay = false;

  layout: LabelLayoutConfig | null = null;
  presets: any[] = [];
  selectedPresetIndex: number = 1;

  selectedElementId: string | null = null;
  isDragging: boolean = false;
  dragMode: DragMode = 'move';
  private renderDebounceTimer: any = null;
  dragStartX = 0;
  dragStartY = 0;
  elementStartX = 0;
  elementStartY = 0;
  elementStartW = 0;
  elementStartH = 0;

  // Configurações avançadas (persistido em localStorage)
  showAdvanced: boolean = false;
  private readonly ADVANCED_KEY = 'niimbot-label-editor-advanced';

  // Fontes disponíveis para seleção
  /** Chaves substituídas no valor do texto na hora de imprimir. */
  readonly variaveisDisponiveis = [
    '{codigoIngresso}',
    '{nomeParticipante}',
    '{nomeLote}',
    '{dt}',
    '{date}',
    '{time}',
  ];

  readonly availableFonts = [
    'Arial', 'Helvetica', 'Times New Roman', 'Courier New',
    'Georgia', 'Verdana', 'Trebuchet MS', 'Impact',
    'Comic Sans MS', 'Lucida Console'
  ];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Delete' || event.key === 'Backspace') {
      // Não deletar se o foco está num input/select/textarea
      const tag = (event.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (this.selectedElementId) {
        event.preventDefault();
        this.deleteSelectedElement();
      }
    }
  }

  ngOnInit(): void {
    this.showAdvanced = localStorage.getItem(this.ADVANCED_KEY) === 'true';
  }

  ngAfterViewInit(): void {
    this.loadPresets();
  }

  toggleAdvanced(): void {
    this.showAdvanced = !this.showAdvanced;
    localStorage.setItem(this.ADVANCED_KEY, String(this.showAdvanced));
  }

  // ── Presets ──

  loadPresets(): void {
    this.http.get<any[]>('assets/print/preset.json').subscribe(
      (data) => { this.presets = data; this.loadLayout(); },
      () => { this.presets = [this.getDefaultPreset()]; this.loadLayout(); }
    );
  }

  private getDefaultPreset(): any {
    return { width: 50, height: 30, unit: 'mm', dpmm: 8, printDirection: 'top', shape: 'rect', title: '50×30mm (padrão)' };
  }

  getPresetTitle(preset: any): string {
    return (preset.title && preset.title.trim()) ? preset.title : `${preset.width}×${preset.height}mm`;
  }

  // ── Deslocamento (calibração do cabeçote) ──

  get deslocamentoX(): number {
    return this.layout?.preset?.offsetX ?? 0;
  }

  get deslocamentoY(): number {
    return this.layout?.preset?.offsetY ?? 0;
  }

  /**
   * Grava a calibração NO PRESET do layout, que é o que vai junto para a
   * impressão e para o localStorage. Não redesenha o canvas de propósito: o
   * preview mostra a etiqueta, e o deslocamento existe justamente porque a
   * máquina não imprime onde o preview diz.
   */
  atualizarDeslocamento(eixo: 'offsetX' | 'offsetY', valor: string): void {
    if (!this.layout) return;
    const n = Number(valor);
    this.layout.preset[eixo] = Number.isFinite(n) ? n : 0;
    this.saveLayout();
  }

  onPresetChange(): void {
    if (!this.layout) return;
    this.layout.preset = this.presets[this.selectedPresetIndex];
    this.saveLayout();
    this.renderCanvas();
  }

  // ── Layout ──

  loadLayout(): void {
    const stored = localStorage.getItem('niimbot-label-layout');
    if (stored) {
      this.layout = JSON.parse(stored);
      if (this.layout && this.presets.length > 0) {
        const savedPreset = this.layout.preset;
        let idx = this.presets.findIndex(p => p.title && p.title === savedPreset.title);
        if (idx < 0) {
          idx = this.presets.findIndex(p => p.width === savedPreset.width && p.height === savedPreset.height);
        }
        if (idx >= 0) this.selectedPresetIndex = idx;
      }
    } else {
      this.createInitialLayout();
    }
    this.renderCanvas();
    if (this.layout) this.layoutChange.emit(this.layout);
  }

  createInitialLayout(): void {
    const preset = this.presets[this.selectedPresetIndex] || this.presets[0];
    if (!preset) { console.error('Nenhum preset disponível no índice', this.selectedPresetIndex); return; }
    const dpmm = preset.dpmm || 8;
    const cw = Math.ceil((preset.width * dpmm) / 8) * 8;
    const ch = preset.height * dpmm;

    this.layout = {
      preset,
      elements: [
        { id: 'qr-1', type: 'qr', x: cw / 2 - 60, y: ch / 2 - 60, width: 120, height: 120, dataSource: 'fixed', value: '' },
        { id: 'text-1', type: 'text', x: cw / 2 - 100, y: 20, width: 200, height: 30, fontSize: 24, wrap: false, dataSource: 'name', value: '' }
      ]
    };
    this.saveLayout();
  }

  saveLayout(): void {
    if (this.layout) {
      localStorage.setItem('niimbot-label-layout', JSON.stringify(this.layout));
      this.layoutChange.emit(this.layout);
    }
  }

  // ── Canvas helpers ──

  private get canvasDims(): { w: number; h: number } {
    if (!this.layout) return { w: 400, h: 240 };
    const d = this.layout.preset.dpmm || 8;
    return { w: Math.ceil((this.layout.preset.width * d) / 8) * 8, h: this.layout.preset.height * d };
  }

  // ── Render ──

  renderCanvas(): void {
    const canvasRef = this.canvasRef();
    if (!canvasRef || !this.layout) return;
    const canvas = canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { w, h } = this.canvasDims;
    canvas.width = w;
    canvas.height = h;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);

    this.layout.elements.forEach(el => this.renderElement(ctx, el));
  }

  renderElement(ctx: CanvasRenderingContext2D, el: LabelElement): void {
    const selected = el.id === this.selectedElementId;

    if (el.type === 'line') {
      this.renderLineElement(ctx, el, selected);
    } else {
      // Borda
      ctx.strokeStyle = selected ? '#007bff' : '#cccccc';
      ctx.lineWidth = selected ? 2 : 1;
      ctx.strokeRect(el.x, el.y, el.width, el.height);

      if (el.type === 'qr') this.renderQRElement(ctx, el);
      else if (el.type === 'text') this.renderTextElement(ctx, el);
    }

    // Resize handles
    if (selected) this.drawResizeHandles(ctx, el);
  }

  renderLineElement(ctx: CanvasRenderingContext2D, el: LabelElement, selected: boolean): void {
    ctx.fillStyle = '#000000';
    ctx.fillRect(el.x, el.y, el.width, el.height);
    if (selected) {
      ctx.strokeStyle = '#007bff';
      ctx.lineWidth = 2;
      ctx.strokeRect(el.x - 1, el.y - 1, el.width + 2, el.height + 2);
    }
  }

  renderQRElement(ctx: CanvasRenderingContext2D, el: LabelElement): void {
    const qrText = this.qrText || el.value || 'SAMPLE-QR-CODE';
    QRCode.toDataURL(qrText, { errorCorrectionLevel: 'M', margin: 1, width: el.width })
      .then((url: string) => {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, el.x, el.y, el.width, el.height);
        img.src = url;
      })
      .catch(() => {
        ctx.fillStyle = '#ff0000';
        ctx.font = '10px Arial';
        ctx.fillText('QR Error', el.x + 5, el.y + 15);
      });
  }

  renderTextElement(ctx: CanvasRenderingContext2D, el: LabelElement): void {
    const fontSize = el.fontSize || 12;
    const fontFamily = el.fontFamily || 'Arial';
    const fontWeight = el.fontWeight || 'normal';
    const fontStyle = el.fontStyle || 'normal';
    const textAlign = el.textAlign || 'left';

    ctx.fillStyle = '#000000';
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.textBaseline = 'top';

    let text = '';
    switch (el.dataSource) {
      case 'fixed': text = el.value || ''; break;
      case 'name': text = this.overrideParticipantName ?? this.participantName ?? '[Nome do Participante]'; break;
      case 'lote': text = this.overrideLoteName ?? this.loteName ?? '[Nome do Lote]'; break;
      case 'extra': text = el.value || '[Campo Editável]'; break;
    }
    text = this.resolveTemplateVariables(text);

    // Calcular posição X conforme alinhamento
    let textX = el.x + 5;
    const maxWidth = el.width - 10;
    if (textAlign === 'center') {
      ctx.textAlign = 'center';
      textX = el.x + el.width / 2;
    } else if (textAlign === 'right') {
      ctx.textAlign = 'right';
      textX = el.x + el.width - 5;
    } else {
      ctx.textAlign = 'left';
    }

    if (el.wrap) {
      this.renderWrappedText(ctx, text, textX, el.y + 4, maxWidth, fontSize, textAlign);
    } else {
      ctx.fillText(text, textX, el.y + 4);
    }

    // Reset
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  renderWrappedText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number, _textAlign?: string): void {
    const words = text.split(' ');
    let line = '';
    let curY = y;
    for (const word of words) {
      const test = line + (line ? ' ' : '') + word;
      if (ctx.measureText(test).width > maxWidth && line) {
        ctx.fillText(line, x, curY);
        line = word;
        curY += lineHeight + 2;
      } else {
        line = test;
      }
    }
    ctx.fillText(line, x, curY);
  }

  // ── Resize handles ──

  private drawResizeHandles(ctx: CanvasRenderingContext2D, el: LabelElement): void {
    const hs = HANDLE_SIZE;
    const handles = this.getHandlePositions(el);
    ctx.fillStyle = '#007bff';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    for (const h of handles) {
      ctx.fillRect(h.x - hs / 2, h.y - hs / 2, hs, hs);
      ctx.strokeRect(h.x - hs / 2, h.y - hs / 2, hs, hs);
    }
  }

  private getHandlePositions(el: LabelElement): { x: number; y: number; mode: DragMode }[] {
    const { x, y, width: w, height: h } = el;
    return [
      { x: x, y: y, mode: 'resize-tl' },
      { x: x + w, y: y, mode: 'resize-tr' },
      { x: x, y: y + h, mode: 'resize-bl' },
      { x: x + w, y: y + h, mode: 'resize-br' },
      { x: x + w / 2, y: y, mode: 'resize-t' },
      { x: x + w / 2, y: y + h, mode: 'resize-b' },
      { x: x, y: y + h / 2, mode: 'resize-l' },
      { x: x + w, y: y + h / 2, mode: 'resize-r' },
    ];
  }

  private hitTestHandle(mx: number, my: number, el: LabelElement): DragMode | null {
    const hs = HANDLE_SIZE;
    for (const h of this.getHandlePositions(el)) {
      if (Math.abs(mx - h.x) <= hs && Math.abs(my - h.y) <= hs) return h.mode;
    }
    return null;
  }

  // ── Template variables ──

  resolveTemplateVariables(text: string): string {
    const now = new Date();
    return text
      .replace(/\{codigoIngresso\}/g, this.qrText || '')
      .replace(/\{nomeParticipante\}/g, this.participantName || '')
      .replace(/\{nomeLote\}/g, this.loteName || '')
      .replace(/\{dt\}/g, now.toISOString().slice(0, 19).replace('T', ' '))
      .replace(/\{date\}/g, now.toISOString().slice(0, 10))
      .replace(/\{time\}/g, now.toTimeString().slice(0, 8));
  }

  // ── Element management ──

  get hasQRElement(): boolean {
    return !!this.layout?.elements.some(e => e.type === 'qr');
  }

  get selectedElement(): LabelElement | null {
    if (!this.layout || !this.selectedElementId) return null;
    return this.layout.elements.find(e => e.id === this.selectedElementId) || null;
  }

  addTextElement(): void {
    if (!this.layout) return;
    const id = `text-${Date.now()}`;
    this.layout.elements.push({
      id, type: 'text', x: 20, y: 20, width: 200, height: 30,
      fontSize: 24, wrap: false, dataSource: 'fixed', value: 'Novo Texto'
    });
    this.selectedElementId = id;
    this.saveLayout();
    this.renderCanvas();
  }

  addQRElement(): void {
    if (!this.layout || this.hasQRElement) return;
    const id = `qr-${Date.now()}`;
    const { w, h } = this.canvasDims;
    this.layout.elements.push({
      id, type: 'qr', x: w / 2 - 60, y: h / 2 - 60, width: 120, height: 120,
      dataSource: 'fixed', value: ''
    });
    this.selectedElementId = id;
    this.saveLayout();
    this.renderCanvas();
  }

  addLineElement(): void {
    if (!this.layout) return;
    const id = `line-${Date.now()}`;
    const { w } = this.canvasDims;
    this.layout.elements.push({
      id, type: 'line', x: 20, y: 100, width: w - 40, height: 4,
      dataSource: 'fixed', value: ''
    });
    this.selectedElementId = id;
    this.saveLayout();
    this.renderCanvas();
  }

  deleteSelectedElement(): void {
    if (!this.layout || !this.selectedElementId) return;
    this.layout.elements = this.layout.elements.filter(e => e.id !== this.selectedElementId);
    this.selectedElementId = null;
    this.saveLayout();
    this.renderCanvas();
  }

  deleteSelected(): void { this.deleteSelectedElement(); }

  updateElementProperty(property: keyof LabelElement, value: any): void {
    if (!this.selectedElement) return;
    (this.selectedElement as any)[property] = value;
    this.saveLayout();
    clearTimeout(this.renderDebounceTimer);
    this.renderDebounceTimer = setTimeout(() => this.renderCanvas(), 150);
  }

  // ── Alignment ──

  alignHorizontalCenter(): void {
    if (!this.selectedElement || !this.layout) return;
    const { w } = this.canvasDims;
    this.selectedElement.x = Math.round((w - this.selectedElement.width) / 2);
    this.saveLayout();
    this.renderCanvas();
  }

  alignVerticalCenter(): void {
    if (!this.selectedElement || !this.layout) return;
    const { h } = this.canvasDims;
    this.selectedElement.y = Math.round((h - this.selectedElement.height) / 2);
    this.saveLayout();
    this.renderCanvas();
  }

  // ── Mouse events ──

  private getCanvasCoords(event: MouseEvent): { x: number; y: number } {
    const canvas = this.canvasRef().nativeElement;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height)
    };
  }

  onCanvasMouseDown(event: MouseEvent): void {
    if (!this.layout) return;
    const { x: mx, y: my } = this.getCanvasCoords(event);

    // Check resize handles on selected element first
    if (this.selectedElement) {
      const handle = this.hitTestHandle(mx, my, this.selectedElement);
      if (handle) {
        this.isDragging = true;
        this.dragMode = handle;
        this.dragStartX = mx;
        this.dragStartY = my;
        this.elementStartX = this.selectedElement.x;
        this.elementStartY = this.selectedElement.y;
        this.elementStartW = this.selectedElement.width;
        this.elementStartH = this.selectedElement.height;
        return;
      }
    }

    // Hit-test elements (top-down)
    for (let i = this.layout.elements.length - 1; i >= 0; i--) {
      const el = this.layout.elements[i];
      if (mx >= el.x && mx <= el.x + el.width && my >= el.y && my <= el.y + el.height) {
        this.selectedElementId = el.id;
        this.isDragging = true;
        this.dragMode = 'move';
        this.dragStartX = mx;
        this.dragStartY = my;
        this.elementStartX = el.x;
        this.elementStartY = el.y;
        this.elementStartW = el.width;
        this.elementStartH = el.height;
        this.renderCanvas();
        return;
      }
    }

    this.selectedElementId = null;
    this.renderCanvas();
  }

  private getCursorForMode(mode: DragMode | null): string {
    switch (mode) {
      case 'resize-tl': case 'resize-br': return 'nwse-resize';
      case 'resize-tr': case 'resize-bl': return 'nesw-resize';
      case 'resize-t': case 'resize-b': return 'ns-resize';
      case 'resize-l': case 'resize-r': return 'ew-resize';
      case 'move': return 'move';
      default: return 'crosshair';
    }
  }

  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.layout) return;
    const { x: mx, y: my } = this.getCanvasCoords(event);
    const canvas = this.canvasRef().nativeElement;

    // Se não está arrastando, apenas atualiza cursor
    if (!this.isDragging) {
      let cursor = 'crosshair';
      if (this.selectedElement) {
        const handle = this.hitTestHandle(mx, my, this.selectedElement);
        if (handle) {
          cursor = this.getCursorForMode(handle);
        } else if (
          mx >= this.selectedElement.x && mx <= this.selectedElement.x + this.selectedElement.width &&
          my >= this.selectedElement.y && my <= this.selectedElement.y + this.selectedElement.height
        ) {
          cursor = 'move';
        }
      }
      // Verificar hover sobre outros elementos
      if (cursor === 'crosshair') {
        for (let i = this.layout.elements.length - 1; i >= 0; i--) {
          const el = this.layout.elements[i];
          if (mx >= el.x && mx <= el.x + el.width && my >= el.y && my <= el.y + el.height) {
            cursor = 'pointer';
            break;
          }
        }
      }
      canvas.style.cursor = cursor;
      return;
    }

    // Dragging
    if (!this.selectedElement) return;
    const dx = mx - this.dragStartX;
    const dy = my - this.dragStartY;
    const { w: cw, h: ch } = this.canvasDims;
    const el = this.selectedElement;
    const MIN = 10;

    if (this.dragMode === 'move') {
      el.x = Math.max(0, Math.min(cw - el.width, this.elementStartX + dx));
      el.y = Math.max(0, Math.min(ch - el.height, this.elementStartY + dy));
    } else {
      let nx = this.elementStartX, ny = this.elementStartY;
      let nw = this.elementStartW, nh = this.elementStartH;

      if (this.dragMode.includes('r')) { nw = Math.max(MIN, this.elementStartW + dx); }
      if (this.dragMode.includes('l')) { nw = Math.max(MIN, this.elementStartW - dx); nx = this.elementStartX + (this.elementStartW - nw); }
      if (this.dragMode.includes('b')) { nh = Math.max(MIN, this.elementStartH + dy); }
      if (this.dragMode.includes('t')) { nh = Math.max(MIN, this.elementStartH - dy); ny = this.elementStartY + (this.elementStartH - nh); }

      el.x = Math.max(0, nx);
      el.y = Math.max(0, ny);
      el.width = Math.min(nw, cw - el.x);
      el.height = Math.min(nh, ch - el.y);
    }

    canvas.style.cursor = this.getCursorForMode(this.dragMode);
    this.renderCanvas();
  }

  onCanvasMouseUp(): void {
    if (this.isDragging) { this.isDragging = false; this.saveLayout(); }
  }

  onCanvasMouseLeave(): void {
    if (this.isDragging) { this.isDragging = false; this.saveLayout(); }
  }

  ngAfterViewChecked(): void {
    const inlineEditorInputRef = this.inlineEditorInputRef();
    if (this.pendingFocusOverlay && inlineEditorInputRef) {
      inlineEditorInputRef.nativeElement.focus();
      inlineEditorInputRef.nativeElement.select();
      this.pendingFocusOverlay = false;
    }
  }

  onCanvasDoubleClick(event: MouseEvent): void {
    if (!this.layout) return;
    const { x: mx, y: my } = this.getCanvasCoords(event);

    for (let i = this.layout.elements.length - 1; i >= 0; i--) {
      const el = this.layout.elements[i];
      if (el.type !== 'text') continue;
      if (el.dataSource !== 'name' && el.dataSource !== 'lote') continue;
      if (mx >= el.x && mx <= el.x + el.width && my >= el.y && my <= el.y + el.height) {
        const canvas = this.canvasRef().nativeElement;
        const canvasRect = canvas.getBoundingClientRect();
        const scaleX = canvasRect.width / canvas.width;
        const scaleY = canvasRect.height / canvas.height;

        const currentValue = el.dataSource === 'name'
          ? (this.overrideParticipantName ?? this.participantName ?? '')
          : (this.overrideLoteName ?? this.loteName ?? '');

        this.editingOverlay = {
          field: el.dataSource as 'name' | 'lote',
          value: currentValue,
          rect: {
            left: canvasRect.left + el.x * scaleX,
            top: canvasRect.top + el.y * scaleY,
            width: Math.max(el.width * scaleX, 80),
            height: Math.max(el.height * scaleY, 28)
          },
          fontSize: Math.min(Math.max((el.fontSize || 12) * scaleX, 11), 28)
        };
        this.pendingFocusOverlay = true;
        return;
      }
    }
  }

  confirmEditingOverlay(): void {
    if (!this.editingOverlay) return;
    const { field, value } = this.editingOverlay;
    if (field === 'name') {
      this.overrideParticipantName = value;
      this.participantNameChange.emit(value);
    } else {
      this.overrideLoteName = value;
      this.loteNameChange.emit(value);
    }
    this.editingOverlay = null;
    this.renderCanvas();
  }

  cancelEditingOverlay(): void {
    this.editingOverlay = null;
  }
}
